import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { Loader2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function AdminUserDetail() {
  const { uid } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/admin/users/${uid}`).then((r) => setData(r.data));
  }, [uid]);

  if (!data) return <AdminLayout title="User"><Loader2 className="w-6 h-6 animate-spin text-white/40" /></AdminLayout>;

  const u = data.user;
  const accuracy = u.questions_answered ? Math.round((u.correct_answers / u.questions_answered) * 100) : 0;

  return (
    <AdminLayout title={u.name} actions={<Button variant="ghost" onClick={() => nav("/admin/users")} className="text-white/70"><ArrowLeft className="w-4 h-4 mr-1" /> All users</Button>}>
      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-1 bg-white/4 border border-white/10 rounded-lg p-6">
          <div className="text-xs font-mono uppercase tracking-widest text-white/50 mb-4">// account</div>
          <dl className="space-y-3 text-sm">
            <div><dt className="text-white/50 text-xs">Email</dt><dd data-testid="user-email">{u.email}</dd></div>
            <div><dt className="text-white/50 text-xs">Joined</dt><dd className="font-mono text-xs">{new Date(u.created_at).toLocaleString()}</dd></div>
            <div><dt className="text-white/50 text-xs">Role</dt><dd>{u.is_admin ? "Admin" : "Student"}{u.blocked && " · Blocked"}</dd></div>
          </dl>
        </div>
        <div className="lg:col-span-2 grid grid-cols-2 gap-4">
          <div className="bg-white/4 border border-white/10 rounded-lg p-5"><div className="text-xs font-mono uppercase text-white/50 mb-2">Exams</div><div className="font-serif text-3xl">{u.exams_taken}</div><div className="text-xs text-white/50 mt-1">{u.exams_passed} passed</div></div>
          <div className="bg-white/4 border border-white/10 rounded-lg p-5"><div className="text-xs font-mono uppercase text-white/50 mb-2">Accuracy</div><div className="font-serif text-3xl">{accuracy}%</div><div className="text-xs text-white/50 mt-1">{u.correct_answers} / {u.questions_answered}</div></div>
        </div>
      </div>

      <h2 className="font-serif text-xl mb-3">Recent exam attempts</h2>
      {data.recent_attempts.length === 0 ? (
        <div className="text-sm text-white/40 italic mb-8">No exams yet.</div>
      ) : (
        <div className="space-y-2 mb-8" data-testid="user-attempts">
          {data.recent_attempts.map((a) => (
            <div key={a.id} className="flex items-center gap-4 px-4 py-3 border border-white/10 rounded-md bg-white/4">
              <div className={`w-2 h-2 rounded-full ${a.passed ? "bg-emerald-500" : "bg-red-500"}`} />
              <div className="flex-1 text-sm">{a.category_code} · {a.correct_count}/{a.total} correct</div>
              <div className="font-mono">{a.score_pct}%</div>
              <div className="text-xs text-white/50 font-mono">{new Date(a.submitted_at).toLocaleDateString()}</div>
            </div>
          ))}
        </div>
      )}

      <h2 className="font-serif text-xl mb-3">Recent answers</h2>
      <div className="space-y-1.5" data-testid="user-answers">
        {data.recent_answers.map((r, idx) => (
          <div key={`${r.question_id}-${idx}`} className="flex items-center gap-3 px-4 py-2 border border-white/8 rounded text-sm">
            <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${r.correct ? "bg-emerald-500" : "bg-red-500"}`} />
            <div className="font-mono text-xs text-white/50 w-24 truncate">{r.question_id}</div>
            <div className="font-mono text-xs">{r.last_letter}</div>
            <div className="flex-1 text-xs text-white/50 font-mono truncate text-right">{r.at ? new Date(r.at).toLocaleString() : ""}</div>
          </div>
        ))}
      </div>
    </AdminLayout>
  );
}
